import S from 's-js';
import { SArray as ArraySignal, lift } from 's-array';
import TZDate from 'lib/TZDate';

import { Event } from 'data/models/Event';
import { EventType } from 'data/models/EventType';
import { PortCall } from 'data/models/PortCall';
import { ScheduleDateEntity as DateEntity } from 'data/models/ScheduleDateEntity';
import { ScheduleDateType as DateType } from 'data/models/ScheduleDateType';

export interface MetroMapMilestoneParams {
    /** The milestone date entity. */
    Date : DateEntity;
}

export interface MilestoneParams extends MetroMapMilestoneParams {
    /**
     * An array signal holding all of this PortCall's milestone dates in order from first (earliest) to last.
     */
    MilestoneDates : ArraySignal<DateEntity>;
}

export interface MetroMapFrameworkParams<TMilestone> {
    PortCall : PortCall;
    MilestoneConstructor : (params : MilestoneParams) => TMilestone;
}

export interface MetroMapFramework<TMilestone> {
    Milestones : ArraySignal<TMilestone>;
}

export function MetroMapFramework<TMilestone>(
    { PortCall, MilestoneConstructor } : MetroMapFrameworkParams<TMilestone>
) : MetroMapFramework<TMilestone> {
    const
        MilestoneDates = lift(S(() => PortCall.MilestoneDates())),
        Milestones = MilestoneDates.map(Date => MilestoneConstructor({ Date, MilestoneDates }));

    return { Milestones };
}

export type MetroMapMilestone = ReturnType<typeof MetroMapMilestone>;
export function MetroMapMilestone(
    { Date } : MetroMapMilestoneParams
) {
    const
        // Either the next milestone date or, if this is the last milestone, the port departure date.
        NextDate = () =>
            Date.NextMilestoneDate() ??
            Date.PortCall()?.Itinerary().find(pt => pt.IsPortDeparture())?.ArrivalEntity() ??
            null,
        // If this milestone date has an actual date logged, then try to find the event representing the logging
        // of that actual, so we can display info about who logged the actual and when.
        ActualLoggedEvent = S(() => {
            const currentActual = Date.Actual();
            if (currentActual === null) return null;
            // NOTE: For now, we're NOT gonna try to support legacy LineTimeActualPortCallChange events here.
            // If the actual value is old enough to precede us adding the event types below, then we just won't
            // display any metadata about who logged that actual when.
            const expectedEventType =
                Date.Type() === DateType.IPArrival   ? EventType.IPArrivalLogged   :
                Date.Type() === DateType.IPDeparture ? EventType.IPDepartureLogged :
                null;
            if (expectedEventType === null) return null;
            // Basically, we just wanna find the most recent event of the expected type.
            const bestGuess = Date.ItineraryPoint()?.Self()?.Events()
                .filter(e => e.Type() === expectedEventType)
                .reduce((mostRecent, next) =>
                    mostRecent === null || next.AboutId() > mostRecent.AboutId() ? next : mostRecent,
                    null as Event | null
                ) ?? null;
            // As a final check, make sure the actual time that was logged matches the current actual time, to
            // defend against the possibility that we might've missed an event somehow.
            const ipv = bestGuess?.ItineraryPointVersion() ?? null;
            if (ipv === null) return null; // Bail if we don't have enough data to perform this check.
            const loggedTime =
                Date.Type() === DateType.IPArrival   ? ipv.Arrival  .Actual() :
                Date.Type() === DateType.IPDeparture ? ipv.Departure.Actual() :
                null;
            return loggedTime === null ? null : TZDate.eq(currentActual, loggedTime) ? bestGuess : null;
        });

    return {
        Date,
        NextDate,
        ActualLoggedEvent
    };
}



// WEBPACK FOOTER //
// ./apps/portcall/metromapframework/model.ts